import React, { useEffect, useState } from 'react';
import { Clock, AlertTriangle, ShieldCheck } from 'lucide-react';

/**
 * Per-article SLA countdown. Reads `updated_at` plus a configurable SLA window
 * (defaults from site settings: 24h) and shows a colour-coded timer.
 */
const REVIEW_STAGES = ['in_review', 'sub_edited', 'legal_review', 'review'];

function fmtRemaining(seconds) {
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  if (abs >= 3600) return `${h}h ${m}m`;
  if (abs >= 60) return `${m}m`;
  return `${abs}s`;
}

export default function SlaTimer({ article, slaHours = 24 }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  if (!article || !REVIEW_STAGES.includes(article.status)) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#5B616E]">
        <ShieldCheck className="w-3.5 h-3.5 text-[#1F7A5A]" />
        Not in a review stage — SLA does not apply.
      </div>
    );
  }

  const updatedAt = new Date(article.updated_at || article.created_at || Date.now()).getTime();
  const deadline = updatedAt + slaHours * 3600 * 1000;
  const remainingSec = Math.round((deadline - now) / 1000);
  const breached = remainingSec < 0;
  const warning = !breached && remainingSec < 3600;

  let bg = '#F5F0E8';
  let border = '#E7E0D6';
  let fg = '#5B616E';
  let label = `${fmtRemaining(remainingSec)} until SLA breach`;

  if (breached) {
    bg = '#FFF1F2';
    border = '#F43F5E';
    fg = '#B3261E';
    label = `Overdue by ${fmtRemaining(remainingSec)}`;
  } else if (warning) {
    bg = '#FFFBEB';
    border = '#F59E0B';
    fg = '#9A6B1A';
  }

  return (
    <div
      className="rounded-lg border p-3 flex items-center gap-2 text-xs font-medium"
      style={{ background: bg, borderColor: border, color: fg }}
      data-testid="article-sla-timer"
      data-sla-status={breached ? 'breached' : warning ? 'warning' : 'ok'}
    >
      {breached ? <AlertTriangle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
      <div className="flex-1">
        <div>{label}</div>
        <div className="text-[10px] opacity-80 font-normal">
          Stage <span className="capitalize">{(article.status || '').replace(/_/g, ' ')}</span> · SLA {slaHours}h
        </div>
      </div>
    </div>
  );
}
